import { useEffect, useMemo, useState } from "react";
import { Toaster } from "react-hot-toast";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { clearAdminSession, getAdminUser, refreshAdminSession } from "../api/adminClient";
import {
  getAdminPdfBranding,
  getAdminWorkspaceDisplayName,
  isClassicAdminWorkspace,
} from "../utils/workspacePdfBranding";
import { useTenantFavicon } from "./useTenantFavicon";
import { NexusProvider, useNexus } from "../ai/NexusProvider";
import { useTheme } from "../context/ThemeContext";
import AdminAssistantDock, { AdminAssistantSearchBar } from "./AdminAssistantDock";
import { isAdminDashboardPath, isAdminNexusChatPath, isAdminNexusHomePath } from "./assistantPageContext";
import { buildWorkspaceChatContext } from "../ai/workspaceContext";
import { ROUTES } from "./routes";
import NexusNavOrb from "./NexusNavOrb";
import AdminAccountMenu from "./AdminAccountMenu";

const RECENT_ROUTES_KEY = "admin-recent-routes";

const BASE_NAV = [
  { path: ROUTES.dashboard, label: "NEXUS", Icon: HomeIcon, exact: true },
  { path: ROUTES.portfolio, label: "Portfolio", Icon: AnalyticsIcon },
  { path: ROUTES.engagement, label: "Engagement", Icon: InviteIcon },
  { path: ROUTES.studio, label: "Studio", Icon: CompanyIcon },
];

const EMAIL_NAV_ITEM = {
  path: ROUTES.adminEmailTemplates,
  label: "Mail Manager",
  Icon: MailIcon,
};
const ADMIN_NAV_ITEM = { path: ROUTES.adminUsers, label: "Admin", Icon: ShieldIcon };

/** Static base nav + optional email scheduler + Admin console. */
function buildNav(permissions = []) {
  const perms = permissions || [];
  const items = [...BASE_NAV];
  if (perms.includes("templates:manage")) {
    items.push(EMAIL_NAV_ITEM);
  }
  const canAccessAdmin = ["admin:read", "admin:manage_users", "admin:manage_roles"].some((p) =>
    perms.includes(p)
  );
  if (canAccessAdmin) items.push(ADMIN_NAV_ITEM);
  return items;
}

const SIDEBAR_COLLAPSED_KEY = "admin-sidebar-collapsed";
const PRODUCT_LOGO_SRC = "/5x-logo-tagline.png";
const DEFAULT_PROFILE_SRC = "/company-logo-placeholder.png";

function routeLabelForPath(pathname) {
  if (isAdminDashboardPath(pathname)) return "NEXUS Command";
  if (isAdminNexusChatPath(pathname)) return "NEXUS Chat";
  if (pathname.includes("/portfolio")) return "Portfolio Intelligence";
  if (pathname.includes("/engagement")) return "Engagement";
  if (pathname.includes("/studio")) return "Workspace Studio";
  if (pathname.includes("/analytics")) return "Portfolio · Operations";
  if (pathname.includes("/insights")) return "Portfolio · Performance";
  if (pathname.includes("/invites")) return "Engagement · Pipeline";
  if (pathname.includes("/invite")) return "Engagement · Compose";
  if (pathname.includes("/company-profile")) return "Studio · Companies";
  if (pathname.includes("/report-profiles")) return "Studio · Reports";
  if (pathname.includes("/assessment-form")) return "Studio · Survey";
  if (pathname.includes("/survey-templates")) return "Studio · Templates";
  if (pathname.includes("/admin-console/email-templates")) return "Mail Manager";
  if (pathname.includes("/admin-console")) return "Admin Console";
  if (pathname.includes("/branding")) return "Workspace branding";
  if (pathname.includes("/switch-workspace")) return "Select your profile";
  return "Admin";
}

function AdminTopNav({
  profileImageSrc,
  brandLogoSrc,
  productDisplayName,
  isClassicWorkspace,
  displayName,
  userEmail,
  workspaceLabel,
  roleName,
  canEditBranding,
  showSwitchWorkspace,
  onLogout,
  onToggleTheme,
  dark,
  showNexusOrb,
  showCommandBar,
  onOpenCommand,
  onSubmitCommand,
  commandBarActive,
  routeLabel,
  onMenuClick,
}) {
  return (
    <nav
      className={`shrink-0 px-3 sm:px-5 py-2.5 border admin-topnav ${
        showCommandBar ? "admin-topnav--surface" : ""
      } ${dark ? "admin-topnav--dark" : "admin-topnav--light"}`}
    >
      <div className="flex items-center w-full min-w-0 gap-2 sm:gap-4">
        <button
          type="button"
          className="admin-mobile-menu-btn lg:hidden"
          onClick={onMenuClick}
          aria-label="Open navigation menu"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        <div className={`admin-topnav-logo shrink-0 ${dark ? "bg-transparent" : ""}`}>
          {brandLogoSrc ? (
            <img
              src={brandLogoSrc}
              alt={productDisplayName || "Organization logo"}
              className="h-9 sm:h-10 w-auto max-w-[min(10rem,26vw)] sm:max-w-[11rem] object-contain transition-transform hover:scale-102"
            />
          ) : isClassicWorkspace ? (
            <img
              src={PRODUCT_LOGO_SRC}
              alt={productDisplayName || "5x Team Assessment"}
              className="h-9 sm:h-10 w-auto max-w-[min(10rem,26vw)] sm:max-w-[11rem] object-contain transition-transform hover:scale-102"
            />
          ) : (
            <span
              className={`admin-topnav-text-brand ${dark ? "admin-topnav-text-brand--dark" : "admin-topnav-text-brand--light"}`}
            >
              <span className="admin-topnav-workspace-name">
                {productDisplayName || workspaceLabel || "Team Assessment"}
              </span>
              <span className="admin-topnav-powered-by">powered by NEXUS</span>
            </span>
          )}
        </div>

        <div className="flex-1 min-w-0 flex justify-center px-1 sm:px-4">
          {showCommandBar ? (
            <div className="w-full max-w-xl lg:max-w-2xl">
              <AdminAssistantSearchBar
                onActivate={onOpenCommand}
                onSubmit={onSubmitCommand}
                routeLabel={routeLabel}
                active={commandBarActive}
              />
            </div>
          ) : null}
        </div>

        <div className="admin-topnav-actions flex items-center gap-2.5 shrink-0 ml-auto">
          {showNexusOrb ? <NexusNavOrb /> : null}
          <button
            type="button"
            onClick={onToggleTheme}
            className={`admin-topnav-icon-btn ${dark ? "admin-topnav-icon-btn--dark" : "admin-topnav-icon-btn--light"}`}
            aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
            title={dark ? "Light mode" : "Dark mode"}
          >
            {dark ? (
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <circle cx="12" cy="12" r="4" />
                <path strokeLinecap="round" d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
              </svg>
            ) : (
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
              </svg>
            )}
          </button>
          <AdminAccountMenu
            profileImageSrc={profileImageSrc || DEFAULT_PROFILE_SRC}
            displayName={displayName}
            userEmail={userEmail}
            workspaceLabel={workspaceLabel}
            roleName={roleName}
            canEditBranding={canEditBranding}
            showSwitchWorkspace={showSwitchWorkspace}
            onLogout={onLogout}
            dark={dark}
          />
        </div>
      </div>
    </nav>
  );
}

function iconClassName(className = "") {
  return `w-5 h-5 shrink-0 ${className}`.trim();
}

function HomeIcon({ className = "" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={iconClassName(className)}
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l9-9 9 9M5 10v10h5v-6h4v6h5V10" />
    </svg>
  );
}

function InviteIcon({ className = "" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={iconClassName(className)}
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 8v6M22 11h-6" />
    </svg>
  );
}

function ListIcon({ className = "" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={iconClassName(className)}
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  );
}

function CompanyIcon({ className = "" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={iconClassName(className)}
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  );
}

function FormIcon({ className = "" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={iconClassName(className)}
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

function ReportIcon({ className = "" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={iconClassName(className)}
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

function AnalyticsIcon({ className = "" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={iconClassName(className)}
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );
}

function InsightsIcon({ className = "" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={iconClassName(className)}
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
    </svg>
  );
}

function MailIcon({ className = "" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={iconClassName(className)}
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
}

function ShieldIcon({ className = "" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={iconClassName(className)}
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
    </svg>
  );
}

function LogoutIcon({ className = "" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={iconClassName(className)}
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
      />
    </svg>
  );
}

function ChevronIcon({ collapsed }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={`w-5 h-5 transition-transform duration-200 ${collapsed ? "rotate-180" : ""}`}
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function CollapsedLabel({ collapsed, children }) {
  return (
    <span
      className={`truncate text-sm whitespace-nowrap transition-all duration-200 ease-in-out ${
        collapsed ? "w-0 opacity-0 overflow-hidden" : "w-auto opacity-100 ml-3"
      }`}
    >
      {children}
    </span>
  );
}

function trackRecentRoute(pathname, label) {
  if (!pathname || isAdminDashboardPath(pathname)) return;
  try {
    const raw = localStorage.getItem(RECENT_ROUTES_KEY);
    const prev = raw ? JSON.parse(raw) : [];
    const list = Array.isArray(prev) ? prev : [];
    const next = [
      { path: pathname, label, at: Date.now() },
      ...list.filter((item) => item.path !== pathname),
    ].slice(0, 8);
    localStorage.setItem(RECENT_ROUTES_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

function isNavActive(pathname, item) {
  if (item.exact) return isAdminNexusHomePath(pathname);
  if (item.path === ROUTES.portfolio) {
    return pathname.includes("/portfolio") || pathname.includes("/analytics") || pathname.includes("/insights");
  }
  if (item.path === ROUTES.engagement) {
    return pathname.includes("/engagement") || pathname.includes("/invite");
  }
  if (item.path === ROUTES.studio) {
    return (
      pathname.includes("/studio") ||
      pathname.includes("/company-profile") ||
      pathname.includes("/assessment-form") ||
      pathname.includes("/report-profiles") ||
      pathname.includes("/survey-templates")
    );
  }
  if (item.path === ROUTES.adminEmailTemplates) {
    return pathname.includes("/admin-console/email-templates");
  }
  if (item.path === ROUTES.adminUsers) {
    return pathname.includes("/admin-console") && !pathname.includes("/admin-console/email-templates");
  }
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
}

function AdminLayoutShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const {
    dockOpen,
    setDockOpen,
    openDock,
    setPageContext,
    focusCommand,
    startNewChat,
    workspaceBrandingRevision,
  } = useNexus();
  const dark = theme === "dark";
  const [sessionUser, setSessionUser] = useState(() => getAdminUser());
  const nav = useMemo(
    () => buildNav(sessionUser?.permissions),
    [sessionUser?.permissions]
  );
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      if (stored === null) return true;
      return stored === "1";
    } catch {
      return true;
    }
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [adminAssistantContext, setAdminAssistantContext] = useState(null);

  const onDashboard = isAdminDashboardPath(location.pathname);
  const onNexusHome = isAdminNexusHomePath(location.pathname);
  const routeLabel = useMemo(
    () => routeLabelForPath(location.pathname),
    [location.pathname]
  );

  useEffect(() => {
    let cancelled = false;
    refreshAdminSession()
      .then((user) => {
        if (!cancelled && user) setSessionUser(user);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setPageContext(
      adminAssistantContext
        ? buildWorkspaceChatContext(adminAssistantContext, location.pathname)
        : null
    );
  }, [adminAssistantContext, location.pathname, setPageContext]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMobileNavOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileNavOpen]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (onDashboard) {
          focusCommand();
        } else {
          openDock();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDashboard, focusCommand, openDock]);

  useEffect(() => {
    const match = nav.find((item) => isNavActive(location.pathname, item));
    if (match) trackRecentRoute(location.pathname, match.label);
  }, [location.pathname, nav]);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  const logout = () => {
    clearAdminSession();
    navigate("/login");
  };

  const displayName = sessionUser?.name?.trim() || sessionUser?.email || "Admin";
  const profileImageSrc = sessionUser?.profilepic || DEFAULT_PROFILE_SRC;
  const workspaceBranding = useMemo(
    () => getAdminPdfBranding(),
    [
      sessionUser?.tenant_id,
      sessionUser?.tenant_name,
      sessionUser?.product_display_name,
      sessionUser?.tenant_logo,
      workspaceBrandingRevision,
    ]
  );
  const tenantLogoSrc =
    workspaceBranding.logo_url || sessionUser?.tenant_logo || "";
  const isClassicWorkspace = isClassicAdminWorkspace(sessionUser);
  const productDisplayName = workspaceBranding.is_classic
    ? getAdminWorkspaceDisplayName()
    : workspaceBranding.product_display_name ||
      sessionUser?.tenant_name?.trim() ||
      "Team Assessment";
  const sidebarOrgLogoSrc = tenantLogoSrc || null;
  const isPlatformAdmin = Boolean(sessionUser?.is_platform_admin);
  const canEditBranding = Boolean(sessionUser?.tenant_id) && !isPlatformAdmin;
  const workspaceLabel =
    productDisplayName || sessionUser?.tenant_name?.trim() || "";

  useTenantFavicon(tenantLogoSrc || null);

  return (
    <div
      data-theme={theme}
      className={`h-screen flex overflow-hidden admin-shell ${dark ? "admin-shell--dark" : "admin-shell--light"}`}
    >
      <button
        type="button"
        className={`admin-sidebar-backdrop ${mobileNavOpen ? "admin-sidebar-backdrop--visible" : ""}`}
        aria-label="Close navigation menu"
        onClick={() => setMobileNavOpen(false)}
      />

      <aside
        className={`admin-sidebar max-lg:w-0 max-lg:min-w-0 ${
          mobileNavOpen ? "admin-sidebar--mobile-open" : ""
        } ${collapsed ? "lg:w-[4.25rem]" : "lg:w-[17rem]"} lg:sticky top-0 shrink-0 flex flex-col overflow-hidden transition-[width,transform] duration-200 ease-in-out ${
          dark ? "admin-sidebar--dark" : "admin-sidebar--light"
        }`}
      >
        <div className={`admin-sidebar-head ${collapsed ? "admin-sidebar-head--collapsed" : ""}`}>
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className={`admin-sidebar-toggle${sidebarOrgLogoSrc && collapsed ? " admin-sidebar-toggle--branded" : ""}`}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarOrgLogoSrc && collapsed ? (
              <img src={sidebarOrgLogoSrc} alt="" className="admin-sidebar-toggle-logo" />
            ) : (
              <ChevronIcon collapsed={collapsed} />
            )}
          </button>
          {!collapsed || mobileNavOpen ? (
            <div className="admin-sidebar-user min-w-0">
              {sidebarOrgLogoSrc ? (
                <img
                  src={sidebarOrgLogoSrc}
                  alt=""
                  className="admin-sidebar-org-logo"
                />
              ) : (
                <img
                  src={profileImageSrc || DEFAULT_PROFILE_SRC}
                  alt=""
                  className="admin-sidebar-user-avatar"
                />
              )}
              <div className="min-w-0">
                <p className="admin-sidebar-user-name">{displayName}</p>
                {sessionUser?.email ? (
                  <p className="admin-sidebar-user-email">{sessionUser.email}</p>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        {!collapsed || mobileNavOpen ? (
          <div className="admin-sidebar-section-label">
            {sessionUser?.tenant_name ? sessionUser.tenant_name : "Workspace"}
          </div>
        ) : null}

        <nav className="admin-sidebar-nav flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
          {nav.map((item) => {
            const active = isNavActive(location.pathname, item);
            const Icon = item.Icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                title={collapsed && !mobileNavOpen ? item.label : undefined}
                onClick={() => {
                  setMobileNavOpen(false);
                  if (item.exact) startNewChat?.();
                }}
                className={`admin-nav-link ${active ? "admin-nav-link--active" : ""} ${
                  collapsed && !mobileNavOpen ? "admin-nav-link--collapsed" : ""
                }`}
              >
                <span className="admin-nav-link-icon">
                  <Icon />
                </span>
                <CollapsedLabel collapsed={collapsed && !mobileNavOpen}>{item.label}</CollapsedLabel>
              </Link>
            );
          })}
        </nav>

        <div className="admin-sidebar-footer">
          <button
            type="button"
            onClick={logout}
            title="Logout"
            className={`admin-nav-link admin-nav-link--logout ${collapsed && !mobileNavOpen ? "admin-nav-link--collapsed" : ""}`}
          >
            <span className="admin-nav-link-icon">
              <LogoutIcon />
            </span>
            <CollapsedLabel collapsed={collapsed && !mobileNavOpen}>Logout</CollapsedLabel>
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
        <div
          className={`sticky top-0 z-30 shrink-0 relative admin-shell-topnav ${
            onDashboard && dark ? "admin-shell-topnav--dashboard" : ""
          }`}
        >
          <AdminTopNav
            profileImageSrc={profileImageSrc}
            brandLogoSrc={tenantLogoSrc ? tenantLogoSrc : null}
            productDisplayName={productDisplayName}
            isClassicWorkspace={isClassicWorkspace}
            displayName={displayName}
            userEmail={sessionUser?.email || ""}
            workspaceLabel={workspaceLabel}
            roleName={sessionUser?.role_name || sessionUser?.role || ""}
            canEditBranding={canEditBranding}
            showSwitchWorkspace
            onLogout={logout}
            dark={dark}
            onToggleTheme={toggleTheme}
            showNexusOrb={!onNexusHome}
            showCommandBar={!onNexusHome}
            onOpenCommand={openDock}
            onSubmitCommand={(query) => openDock(query)}
            commandBarActive={dockOpen}
            routeLabel={routeLabel}
            onMenuClick={() => setMobileNavOpen(true)}
          />
          {!onNexusHome ? (
            <AdminAssistantDock
              pathname={`${location.pathname}${location.search}`}
              context={adminAssistantContext}
              open={dockOpen}
              onOpenChange={setDockOpen}
            />
          ) : null}
        </div>
        <main className="flex-1 min-h-0 min-w-0 w-full overflow-y-auto overflow-x-hidden admin-main">
          <Outlet context={{ setAdminAssistantContext, sidebarCollapsed: collapsed }} />
        </main>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  return (
    <NexusProvider>
      <Toaster position="top-center" containerStyle={{ zIndex: 100050 }} />
      <AdminLayoutShell />
    </NexusProvider>
  );
}
